import HeroSection from "./components/HeroSection";
import HowUrgentSection from "./components/HowUrgentSection";
import WhyGovernmentWontWork from "./components/WhyGovernmentWontWork";
import HowWeSolveIt from "./components/HowWeSolveIt";
import WhatTypeSection from "./components/WhatTypeSection";
import ProcessingSpeedsSection from "./components/ProcessingSpeedsSection";
import WhatWontWorkSection from "./components/WhatWontWorkSection";
import FAQSection, { faqs } from "./components/FAQSection";
import FinalCTASection from "./components/FinalCTASection";
import RelatedPassportServices from "@/components/feature/RelatedPassportServices";

const pageUrl = "https://fastpassportcenter.com/passport-services/urgent-passport/";

const faqSchemaItems = faqs.map((faq) => ({
  "@type": "Question",
  name: faq.question,
  acceptedAnswer: {
    "@type": "Answer",
    text: faq.answer,
  },
}));

const serviceSchema = {
  "@type": "Service",
  name: "Urgent Passport Service",
  provider: {
    "@type": "Organization",
    name: "Fast Passport Center",
    url: "https://fastpassportcenter.com",
    telephone: "1-877-253-0084",
    address: {
      "@type": "PostalAddress",
      streetAddress: "1629 K Street, Suite 300",
      addressLocality: "Washington",
      addressRegion: "DC",
      postalCode: "20006",
    },
  },
  description: "Urgent passport processing in as little as 3 business days from a registered US Department of State passport courier with daily reserved agency access.",
  areaServed: "US",
  offers: {
    "@type": "Offer",
    price: "195",
    priceCurrency: "USD",
    description: "Urgent passport service starting from $195 depending on processing speed required",
  },
};

const breadcrumbSchema = {
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com" },
    { "@type": "ListItem", position: 2, name: "Passport Services", item: "https://fastpassportcenter.com/passport-services/" },
    { "@type": "ListItem", position: 3, name: "Urgent Passport", item: pageUrl },
  ],
};

const graph = [
  {
    "@type": "Organization",
    name: "Fast Passport Center",
    url: "https://fastpassportcenter.com",
    telephone: "+1-877-253-0084",
    email: "contactus@fastpassportcenter.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "1629 K Street, Suite 300",
      addressLocality: "Washington",
      addressRegion: "DC",
      postalCode: "20006",
      addressCountry: "US",
    },
    areaServed: { "@type": "Country", name: "United States" },
    priceRange: "$$$",
    openingHours: ["Mo-Fr 09:00-18:00"],
    aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
  },
  serviceSchema,
  breadcrumbSchema,
  { "@type": "FAQPage", mainEntity: faqSchemaItems },
];

const jsonLd = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });

function UrgentPassportSEOHead() {
  return (
    <>
      <title>Urgent Passport Service — Get Your Passport in Days, Not Weeks | Fast Passport Center</title>
      <meta name="description" content="Need a passport urgently? Fast Passport Center is a registered State Dept courier with daily agency access. Urgent passport processing in as little as 3 business days. A+ BBB, 20+ years. Start now." />
      <meta name="keywords" content="urgent passport, urgent passport service, need passport urgently, urgent passport renewal, passport urgent processing, how to get a passport urgently, urgent passport courier, passport needed urgently" />
      <meta name="last-modified" content="2026-07-01" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Urgent Passport Service | Fast Passport Center" />
      <meta property="og:description" content="Need a passport urgently? Registered State Dept courier with daily reserved agency access. Processing in as little as 3 business days. A+ BBB, 20+ years." />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content="Fast Passport Center" />
      <meta property="og:image" content="https://fastpassportcenter.com/og-image.jpg" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Urgent Passport Service | Fast Passport Center" />
      <meta name="twitter:description" content="Need a passport urgently? Registered State Dept courier with daily reserved agency access. Processing in as little as 3 business days. A+ BBB, 20+ years." />
      <meta name="twitter:image" content="https://fastpassportcenter.com/og-image.jpg" />
      <link rel="canonical" href={pageUrl} />
      <script type="application/ld+json" id="structured-data-urgent-passport" dangerouslySetInnerHTML={{ __html: jsonLd }} />
    </>
  );
}

export default function UrgentPassportPage() {
  return (
    <div className="font-sans">
      <UrgentPassportSEOHead />
      <HeroSection />
      <HowUrgentSection />
      <WhyGovernmentWontWork />
      <HowWeSolveIt />
      <WhatTypeSection />
      <ProcessingSpeedsSection />
      <WhatWontWorkSection />
      <FAQSection />

      <RelatedPassportServices
        services={[
          {
            title: "Emergency Passport",
            description: "Traveling in less than 72 hours? Emergency processing as fast as 24 hours. Call now.",
            link: "/passport-services/emergency-passport/",
            icon: "ri-alert-line",
          },
          {
            title: "Fast Passport Renewal",
            description: "Just need to renew? No office visit required for eligible renewals. As fast as 3 days.",
            link: "/passport-services/fast-passport-renewal/",
            icon: "ri-refresh-line",
          },
          {
            title: "Expedited Passport Service",
            description: "All speed tiers at a glance. Compare 3-day through 3-5 week processing options.",
            link: "/passport-services/expedited-passport-service/",
            icon: "ri-speed-up-line",
          },
          {
            title: "Passport Expediting Service",
            description: "Learn how our registered courier system works and why we're 20-80% less than competitors.",
            link: "/passport-services/passport-expediting-service/",
            icon: "ri-government-line",
          },
        ]}
        colorClass="text-[#C7510F]"
        bgClass="bg-[#FFF7F0]"
        borderClass="border-[#FDE8D5]"
        linkColorClass="text-[#C7510F]"
      />

      <FinalCTASection />

      {/* Footer */}
      <footer className="bg-[#2D2D2D] text-gray-300 py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Fast Passport Center</h4>
              <p className="text-gray-400 text-sm leading-relaxed mb-3">
                Registered U.S. Department of State passport courier. A+ BBB rated. 20+ years of passport and visa experience.
              </p>
              <p className="text-gray-500 text-xs">
                1629 K Street, Suite 300<br />Washington, DC 20006
              </p>
            </div>
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/passport-services/urgent-passport/" className="hover:text-white transition-colors cursor-pointer">Urgent Passport</a></li>
                <li><a href="/passport-services/emergency-passport/" className="hover:text-white transition-colors cursor-pointer">Emergency Passport</a></li>
                <li><a href="/passport-services/fast-passport-renewal/" className="hover:text-white transition-colors cursor-pointer">Fast Passport Renewal</a></li>
                <li><a href="/passport-services/expedited-passport-service/" className="hover:text-white transition-colors cursor-pointer">Expedited Passport Service</a></li>
                <li><a href="/passport-services/new-passport/" className="hover:text-white transition-colors cursor-pointer">New Passport</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="tel:8772530084" className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">877-253-0084</a></li>
                <li><a href="mailto:contactus@fastpassportcenter.com" className="hover:text-white transition-colors cursor-pointer">Contact Us</a></li>
                <li><a href="/helpful-resources/" className="hover:text-white transition-colors cursor-pointer">Helpful Resources</a></li>
                <li><a href="/helpful-resources/passport-processing-times/" className="hover:text-white transition-colors cursor-pointer">Processing Times</a></li>
                <li><a href="/helpful-resources/passport-fees/" className="hover:text-white transition-colors cursor-pointer">Passport Fees</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Trust &amp; Credentials</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center gap-2">
                  <i className="ri-shield-check-line"></i> A+ BBB Accredited
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-government-fill"></i> Registered State Dept. Courier
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-history-line"></i> 20+ Years Experience
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-lock-line"></i> Secure &amp; Confidential
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 text-center text-xs text-gray-500">
            <p>&copy; {new Date().getFullYear()} Fast Passport Center. All rights reserved. Fast Passport Center is a private passport courier service registered with the U.S. Department of State. We are not a government agency.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}